import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { libraryAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import LibraryMarkdown from '../../components/library/LibraryMarkdown';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';
import TableOfContents from '../../components/library/TableOfContents';

const DOC_TYPE_LABEL = {
  constitution: 'Constitution',
  act: 'Act',
  si: 'Statutory Instrument',
  regulation: 'Regulation',
  rules: 'Rules of Court',
  case: 'Case',
  other: 'Document',
};

const TYPE_BADGE = 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300';

const LibraryDocumentViewer = () => {
  const { docId } = useParams();
  const { user } = useAuth();
  const isStudent = user?.role === 'student';

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tocOpen, setTocOpen] = useState(false);

  // "Explain this section" state
  const [panelOpen, setPanelOpen] = useState(false);
  const [passage, setPassage] = useState('');
  const [explaining, setExplaining] = useState(false);
  const [explanation, setExplanation] = useState(null); // { answer, mode, review, citations }
  const [explainError, setExplainError] = useState(''); // generic (red)
  const [quotaError, setQuotaError] = useState(''); // 403 quota (amber)
  const [selectHint, setSelectHint] = useState(false);

  const handleExplain = async () => {
    const selected = (window.getSelection()?.toString() || '').trim();
    if (!selected) {
      setSelectHint(true);
      setPanelOpen(true);
      return;
    }
    setSelectHint(false);
    setPassage(selected);
    setPanelOpen(true);
    setExplaining(true);
    setExplanation(null);
    setExplainError('');
    setQuotaError('');
    try {
      const res = await libraryAPI.explainSection(docId, selected);
      setExplanation(res.data || null);
    } catch (err) {
      if (err?.response?.status === 403) {
        setQuotaError(
          err.response?.data?.detail ||
            "You've reached your daily AI limit. Please try again later.",
        );
      } else {
        setExplainError('Could not generate an explanation. Please try again.');
      }
    } finally {
      setExplaining(false);
    }
  };

  const closePanel = () => {
    setPanelOpen(false);
    setSelectHint(false);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await libraryAPI.getDocument(docId);
        if (active) setDoc(res.data || null);
      } catch (err) {
        if (active) {
          setError(
            err?.response?.status === 404
              ? 'This document could not be found.'
              : 'Could not load this document. Please try again.',
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [docId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-4xl">
        <Link to="/library" className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4">
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to library
        </Link>
        <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 p-4 rounded-md text-sm">
          {error || 'Document not available.'}
        </div>
      </div>
    );
  }

  const sections = Array.isArray(doc.sections) ? doc.sections : [];
  const hasToc = sections.length > 0;

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-4xl">
      <Link to="/library" className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4">
        <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to library
      </Link>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white break-words">
            {doc.title}
          </h1>
          <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${TYPE_BADGE}`}>
            {DOC_TYPE_LABEL[doc.doc_type] || 'Document'}
          </span>
        </div>
        {doc.citation && (
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{doc.citation}</p>
        )}
        {(doc.jurisdiction || doc.attribution) && (
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            {[doc.jurisdiction, doc.attribution].filter(Boolean).join(' · ')}
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {doc.content && (
            <button
              type="button"
              onClick={handleExplain}
              className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-900"
            >
              <SparklesIcon className="h-4 w-4 mr-1.5" /> Explain selection
            </button>
          )}
          {doc.file_url && (
            <a
              href={doc.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" /> Download original
            </a>
          )}
        </div>
        {doc.content && (
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            Select a passage in the text below, then choose &ldquo;Explain selection&rdquo;.
          </p>
        )}
      </div>

      {/* Mobile collapsible TOC */}
      {hasToc && (
        <div className="lg:hidden mb-5 bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10">
          <button
            type="button"
            onClick={() => setTocOpen((o) => !o)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            <span>Table of contents</span>
            {tocOpen ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </button>
          {tocOpen && (
            <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700 pt-3">
              <TableOfContents sections={sections} />
            </div>
          )}
        </div>
      )}

      {/* Two-column layout on lg, single column on mobile */}
      <div className={hasToc ? 'lg:flex lg:gap-8' : ''}>
        {hasToc && (
          <aside className="hidden lg:block lg:w-64 lg:flex-shrink-0 order-last">
            <div className="sticky top-20">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-4 max-h-[calc(100vh-6rem)] overflow-y-auto">
                <TableOfContents sections={sections} />
              </div>
            </div>
          </aside>
        )}

        <article className="min-w-0 flex-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-4 sm:p-6">
            {doc.content ? (
              <LibraryMarkdown content={doc.content} />
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No readable text is available for this document
                {doc.file_url ? ' — use the download link above.' : '.'}
              </p>
            )}
          </div>
          {doc.source_url && (
            <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
              Source:{' '}
              <a
                href={doc.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-600 dark:hover:text-gray-300"
              >
                {doc.source || doc.source_url}
              </a>
            </p>
          )}
        </article>
      </div>

      {/* Explain panel: right-side drawer on lg, modal/sheet on mobile */}
      {panelOpen && (
        <div className="fixed inset-0 z-40 flex justify-end">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closePanel}
            aria-hidden="true"
          />
          <div className="relative z-10 w-full max-w-md h-full bg-white dark:bg-gray-800 shadow-xl flex flex-col">
            <div className="flex items-start justify-between gap-3 px-4 sm:px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 min-w-0">
                <SparklesIcon className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                  {explanation?.mode === 'professional'
                    ? 'Professional analysis'
                    : explanation?.mode === 'tutor'
                    ? 'Study explanation'
                    : 'Explain this section'}
                </h2>
              </div>
              <button
                type="button"
                onClick={closePanel}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-4">
              {selectHint && (
                <div className="bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 p-3 rounded-md text-sm">
                  Select a passage first, then choose &ldquo;Explain selection&rdquo;.
                </div>
              )}

              {passage && (
                <blockquote className="border-l-2 border-primary-300 dark:border-primary-700 pl-3 text-sm italic text-gray-600 dark:text-gray-300 max-h-32 overflow-y-auto">
                  &ldquo;{passage.length > 320 ? `${passage.slice(0, 320)}…` : passage}&rdquo;
                </blockquote>
              )}

              {explaining && (
                <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary-500" />
                  {isStudent ? 'Preparing your study explanation…' : 'Analyzing the passage…'}
                </div>
              )}

              {quotaError && (
                <div className="bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 p-3 rounded-md text-sm">
                  {quotaError}
                </div>
              )}

              {explainError && (
                <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 p-3 rounded-md text-sm">
                  {explainError}
                </div>
              )}

              {explanation?.answer && (
                <div>
                  {explanation.review && explanation.review.approved === false && (
                    <div className="mb-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 px-3 py-2 rounded-md text-xs">
                      Surfaced with caution — this explanation did not fully pass automated review.
                    </div>
                  )}
                  <MarkdownRenderer content={explanation.answer} />
                  {Array.isArray(explanation.citations) && explanation.citations.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                        Citations
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-xs text-gray-600 dark:text-gray-300">
                        {explanation.citations.map((c, i) => (
                          <li key={i} className="break-words">
                            {typeof c === 'string' ? c : c?.title || c?.citation || JSON.stringify(c)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibraryDocumentViewer;
